import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import mammoth from "mammoth";
import DOMPurify from "dompurify";
import * as XLSX from "xlsx";
import heic2any from "heic2any";
import Button from "../ui/Button";
import LoadingSpinner from "../ui/LoadingSpinner";
import { getSignedDownloadUrl } from "../../api/s3";
import styles from "./FilePreviewModal.module.css";

/** Injected into excel iframe HTML (modules cannot style iframe document) */
const EXCEL_PREVIEW_CSS = `
.excel-preview-container{padding:20px;background-color:#fff}
.excel-sheet-header{margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #dee2e6}
.excel-sheet-name{font-size:18px;font-weight:600;color:#212529;margin:0}
.excel-sheet-content{margin-bottom:32px;overflow-x:auto}
.excel-sheet-content table{width:100%;border-collapse:collapse;font-size:13px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif}
.excel-sheet-content table th,.excel-sheet-content table td{border:1px solid #dee2e6;padding:8px 12px;text-align:left;vertical-align:top}
.excel-sheet-content table th{background-color:#f8f9fa;font-weight:600;color:#495057}
.excel-sheet-content table tr:hover{background-color:#f8f9fa}
.excel-sheet-content table td{color:#212529}
.excel-sheet-content table td:empty::before{content:"\\00a0"}
.excel-truncation-notice{background-color:#fff3cd;border:1px solid #ffc107;border-radius:4px;padding:12px 16px;margin-bottom:16px;color:#856404}
.excel-truncation-notice p{margin:0;font-size:14px;line-height:1.5}
.excel-truncation-notice strong{font-weight:600}
`.trim();

/** Injected into DOCX iframe HTML — Mammoth output is unstyled after sanitize strips style attrs */
const DOCX_PREVIEW_CSS = `
html,body{margin:0;padding:0;background:#fff}
body{padding:28px 32px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;font-size:15px;line-height:1.6;color:#212529}
h1,h2,h3,h4,h5,h6{font-weight:700;line-height:1.3;margin:1.2em 0 0.5em;color:#212529}
h1{font-size:1.75em}h2{font-size:1.4em}h3{font-size:1.2em}
p{margin:0 0 0.85em}
ul,ol{margin:0 0 0.85em;padding-left:1.5em}
li{margin:0.25em 0}
a{color:#0d6efd}
table{width:100%;border-collapse:collapse;margin:0 0 1em;font-size:13px}
th,td{border:1px solid #dee2e6;padding:8px 12px;text-align:left;vertical-align:top}
th{background:#f8f9fa;font-weight:600}
img{max-width:100%;height:auto}
blockquote{margin:0 0 0.85em;padding:8px 16px;border-left:3px solid #dee2e6;color:#495057}
`.trim();

interface FilePreviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    fileUrl: string;
    fileName: string;
    fileType?: string;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ isOpen, onClose, fileUrl, fileName, fileType }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [signedUrl, setSignedUrl] = useState<string>("");
    const [objectUrl, setObjectUrl] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [docxHtml, setDocxHtml] = useState<string | null>(null);
    const [excelHtml, setExcelHtml] = useState<string | null>(null);
    const [isConvertingHeic, setIsConvertingHeic] = useState(false);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    // Helper functions defined before hooks
    const getFileExtension = useCallback((filename: string): string => {
        return filename.split(".").pop()?.toLowerCase() || "";
    }, []);

    const determineFileType = useCallback((): "image" | "pdf" | "text" | "docx" | "excel" | "unsupported" => {
        const ext = getFileExtension(fileName);

        // Supported file formats based on acceptedFileTypes
        // PDF files
        if (ext === "pdf") return "pdf";

        // Image files (including webp, heic)
        if (["jpg", "jpeg", "png", "webp", "heic"].includes(ext)) return "image";

        // Text files (txt only - csv handled as excel)
        if (ext === "txt") return "text";

        // Word documents (.docx)
        if (ext === "docx") return "docx";

        // Excel files (.xls, .xlsx) and CSV files
        if (["xls", "xlsx", "csv"].includes(ext)) return "excel";

        // Any other format is unsupported
        return "unsupported";
    }, [fileName, getFileExtension]);

    // Process DOCX file from blob with Mammoth and DOMPurify
    const processDocxFromBlob = useCallback(
        async (blob: Blob) => {
            const ext = getFileExtension(fileName).toLowerCase();

            // Security checks
            if (ext === "docm") {
                setError("Macro-enabled files (.docm) are not allowed.");
                return;
            }

            if (ext !== "docx") {
                return;
            }

            try {
                const arrayBuffer = await blob.arrayBuffer();

                // Convert DOCX to HTML with inline images
                const options = {
                    convertImage: mammoth.images.imgElement(async function (image: any) {
                        const base64 = await image.read("base64");
                        return {
                            src: `data:${image.contentType};base64,${base64}`,
                        };
                    }),
                };

                const result = await mammoth.convertToHtml({ arrayBuffer }, options);
                const rawHtml = result.value || "";

                // Security: Sanitize the HTML with strict rules
                const clean = DOMPurify.sanitize(rawHtml, {
                    // Block all event handlers and risky attributes
                    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onerror", "style"],
                    // Only allow data: URIs (Mammoth already converts images to base64 data URIs)
                    ALLOWED_URI_REGEXP: /^data:/i,
                    // Block potentially dangerous tags
                    FORBID_TAGS: ["script", "iframe", "object", "embed", "link", "style", "form", "input", "button"],
                });

                setDocxHtml(`<style>${DOCX_PREVIEW_CSS}</style><div class="docx-preview-body">${clean}</div>`);
            } catch (err: any) {
                console.error("DOCX conversion error:", err);
                setError("Failed to convert DOCX file. The file may be corrupted or in an unsupported format.");
            }
        },
        [fileName, getFileExtension],
    );

    // Process Excel/CSV file from blob with SheetJS
    const processExcelFromBlob = useCallback(
        async (blob: Blob) => {
            const ext = getFileExtension(fileName).toLowerCase();

            if (!["xls", "xlsx", "csv"].includes(ext)) {
                return;
            }

            try {
                const arrayBuffer = await blob.arrayBuffer();

                // Read the workbook (SheetJS handles CSV, XLS, and XLSX)
                const workbook = XLSX.read(arrayBuffer, { type: "array" });

                // Maximum rows to preview (to prevent performance issues)
                const MAX_PREVIEW_ROWS = 100;

                // Convert all sheets to HTML
                let html = `<style>${EXCEL_PREVIEW_CSS}</style><div class="excel-preview-container">`;

                workbook.SheetNames.forEach((sheetName, index) => {
                    const worksheet = workbook.Sheets[sheetName];

                    // Get the range of the worksheet
                    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
                    const totalRows = range.e.r - range.s.r + 1; // Total number of rows
                    const isTruncated = totalRows > MAX_PREVIEW_ROWS;

                    // Add sheet name as a header (skip for CSV as it typically has one unnamed sheet)
                    if (ext !== "csv" || workbook.SheetNames.length > 1) {
                        html += `<div class="excel-sheet-header">
            <h3 class="excel-sheet-name">${DOMPurify.sanitize(sheetName)}</h3>
          </div>`;
                    }

                    // If the sheet is too large, truncate it
                    let worksheetToDisplay = worksheet;
                    if (isTruncated) {
                        // Create a limited range (first MAX_PREVIEW_ROWS rows)
                        const limitedRange = {
                            s: { r: range.s.r, c: range.s.c },
                            e: { r: range.s.r + MAX_PREVIEW_ROWS - 1, c: range.e.c },
                        };

                        // Create a new worksheet with limited data
                        worksheetToDisplay = {};
                        worksheetToDisplay["!ref"] = XLSX.utils.encode_range(limitedRange);

                        // Copy cells within the limited range
                        for (let R = limitedRange.s.r; R <= limitedRange.e.r; ++R) {
                            for (let C = limitedRange.s.c; C <= limitedRange.e.c; ++C) {
                                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                                if (worksheet[cellAddress]) {
                                    worksheetToDisplay[cellAddress] = worksheet[cellAddress];
                                }
                            }
                        }

                        // Add truncation notice with translations
                        const limitedText =
                            t("previewLimitedToRows", { count: MAX_PREVIEW_ROWS }) ||
                            `Preview limited to first ${MAX_PREVIEW_ROWS} rows`;
                        const totalText = t("totalRowsInFile", { count: totalRows }) || `Total: ${totalRows} rows`;
                        const downloadText = t("downloadToViewAllData") || "Download the file to view all data.";

                        html += `<div class="excel-truncation-notice">
            <p><strong>${DOMPurify.sanitize(limitedText)}</strong> (${DOMPurify.sanitize(
                totalText,
            )}). ${DOMPurify.sanitize(downloadText)}</p>
          </div>`;
                    }

                    // Convert sheet to HTML table
                    const sheetHtml = XLSX.utils.sheet_to_html(worksheetToDisplay, {
                        id: `sheet-${index}`,
                    });

                    // Sanitize the HTML
                    const cleanSheetHtml = DOMPurify.sanitize(sheetHtml, {
                        ALLOWED_TAGS: ["table", "thead", "tbody", "tr", "th", "td"],
                        ALLOWED_ATTR: ["id"],
                    });

                    html += `<div class="excel-sheet-content">${cleanSheetHtml}</div>`;
                });

                html += "</div>";

                setExcelHtml(html);
            } catch (err: any) {
                console.error("Excel/CSV conversion error:", err);
                setError("Failed to convert file. The file may be corrupted or in an unsupported format.");
            }
        },
        [fileName, getFileExtension, t],
    );

    // Fetch file and process based on type with security checks
    useEffect(() => {
        let createdObjectUrl: string | null = null;

        const fetchAndProcessFile = async () => {
            if (!isOpen || !fileUrl) return;

            setLoading(true);
            setError(null);
            setSignedUrl("");
            setObjectUrl("");
            setDocxHtml(null);
            setExcelHtml(null);
            setIsConvertingHeic(false);

            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    throw new Error("No authentication token found");
                }

                // Get signed URL (short-lived, server validates ownership)
                const response = await getSignedDownloadUrl({ fileUrl, mode: "inline" }, token);
                setSignedUrl(response.signedUrl);

                // Fetch the file as blob
                const fileResponse = await fetch(response.signedUrl);
                if (!fileResponse.ok) {
                    throw new Error("Failed to fetch file");
                }

                // Check Content-Type header for PDFs
                // Old files uploaded without proper metadata have 'application/octet-stream' which forces download
                const ext = getFileExtension(fileName).toLowerCase();
                if (ext === "pdf") {
                    const contentType =
                        fileResponse.headers.get("Content-Type") || fileResponse.headers.get("content-type");

                    // If content-type is octet-stream, the browser will download instead of display
                    if (contentType && contentType.toLowerCase().includes("octet-stream")) {
                        setError(
                            t("previewNotAvailableForOldFiles") ||
                                "Preview not available for this file. It was uploaded in a format that requires download. Please use the download button below.",
                        );
                        setSignedUrl(response.signedUrl); // Keep signed URL for download button
                        setLoading(false);
                        return;
                    }

                    // Also check for attachment disposition (though S3 might not return it via CORS)
                    const contentDisposition =
                        fileResponse.headers.get("Content-Disposition") ||
                        fileResponse.headers.get("content-disposition");
                    if (contentDisposition && contentDisposition.toLowerCase().includes("attachment")) {
                        setError(
                            t("previewNotAvailableForOldFiles") ||
                                "Preview not available for this file. It was uploaded in a format that requires download. Please use the download button below.",
                        );
                        setSignedUrl(response.signedUrl);
                        setLoading(false);
                        return;
                    }
                }

                let blob = await fileResponse.blob();

                // Handle different file types
                if (ext === "docx") {
                    // Process DOCX with Mammoth
                    await processDocxFromBlob(blob);
                } else if (["xls", "xlsx", "csv"].includes(ext)) {
                    // Process Excel/CSV with SheetJS
                    await processExcelFromBlob(blob);
                } else if (["heic"].includes(ext)) {
                    console.log("[preview] HEIC: starting conversion…");
                    setIsConvertingHeic(true);
                    try {
                        // maybe your bundler needs a dynamic import — log it:
                        console.log("[preview] HEIC: importing heic2any…");
                        // const { default: heic2any } = await import('heic2any'); // optional
                        console.log("[preview] HEIC: blob before fix", blob.type, blob.size);

                        const heicBlob = new Blob([blob], {
                            type: "image/heic",
                        });
                        console.log("[preview] HEIC: blob after fix", heicBlob.type, heicBlob.size);

                        const converted = await heic2any({
                            blob: heicBlob,
                            toType: "image/jpeg",
                            quality: 0.9,
                        });
                        console.log("[preview] HEIC: conversion result", converted);

                        const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
                        console.log("[preview] HEIC: jpegBlob", {
                            size: jpegBlob.size,
                            type: jpegBlob.type,
                        });

                        const url = URL.createObjectURL(jpegBlob);
                        console.log("[preview] HEIC: object url created", url);
                        setObjectUrl(url);
                    } catch (conversionError: any) {
                        console.error("[preview] HEIC conversion error", conversionError);
                        setError("Failed to preview HEIC image, please download.");
                    } finally {
                        setIsConvertingHeic(false);
                    }
                } else {
                    // For PDF, images, and text files, create object URL
                    const url = URL.createObjectURL(blob);
                    createdObjectUrl = url;
                    setObjectUrl(url);
                }
            } catch (err) {
                console.error("Error fetching file:", err);
                setError(t("errorLoadingPreview") || "Error loading preview");
            } finally {
                setLoading(false);
            }
        };

        fetchAndProcessFile();

        // Cleanup: revoke object URL when component unmounts or modal closes
        return () => {
            if (createdObjectUrl) {
                URL.revokeObjectURL(createdObjectUrl);
            }
        };
    }, [isOpen, fileUrl, t, fileName, getFileExtension, processDocxFromBlob, processExcelFromBlob]);

    if (!isOpen) return null;

    const renderPreview = () => {
        if (loading || isConvertingHeic) {
            return (
                <div className={styles.loading}>
                    <LoadingSpinner />
                    <p>
                        {isConvertingHeic
                            ? t("convertingHeicImage") || "Converting HEIC image..."
                            : t("loadingPreview") || "Loading preview..."}
                    </p>
                </div>
            );
        }

        if (error) {
            return (
                <div className={styles.error}>
                    <p>{error}</p>
                    {signedUrl && (
                        <Button
                            label={t("download") || "Download"}
                            onClick={() => {
                                const link = document.createElement("a");
                                link.href = signedUrl;
                                link.download = fileName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            variant="primary"
                        />
                    )}
                </div>
            );
        }

        if (!objectUrl && !docxHtml && !excelHtml) {
            return null;
        }

        const type = determineFileType();

        switch (type) {
            case "image":
                return (
                    <div className={styles.imageContainer}>
                        <img src={objectUrl} alt={fileName} className={styles.image} />
                    </div>
                );

            case "pdf":
                return (
                    <div className={styles.pdfContainer}>
                        <iframe src={objectUrl} className={styles.pdf} title={fileName} />
                    </div>
                );

            case "text":
                return (
                    <div className={styles.textContainer}>
                        <iframe src={objectUrl} className={styles.text} title={fileName} />
                    </div>
                );

            case "docx":
                if (!docxHtml) {
                    return (
                        <div className={styles.loading}>
                            <LoadingSpinner />
                            <p>{t("loadingPreview") || "Loading preview..."}</p>
                        </div>
                    );
                }
                return (
                    <div className={styles.docxContainer}>
                        {/* Security: Render sanitized HTML in maximally isolated iframe */}
                        <iframe title="docx-preview" sandbox="" className={styles.docx} srcDoc={docxHtml} />
                    </div>
                );

            case "excel":
                if (!excelHtml) {
                    return (
                        <div className={styles.loading}>
                            <LoadingSpinner />
                            <p>{t("loadingPreview") || "Loading preview..."}</p>
                        </div>
                    );
                }
                return (
                    <div className={styles.excelContainer}>
                        {/* Security: Render sanitized HTML in maximally isolated iframe */}
                        <iframe title="excel-preview" sandbox="" className={styles.excel} srcDoc={excelHtml} />
                    </div>
                );

            case "unsupported":
            default:
                return (
                    <div className={styles.unsupported}>
                        <p>{t("previewNotSupported") || "Preview not supported for this file type."}</p>
                        <p className={styles.filename}>{fileName}</p>
                        <p className={styles.hint}>
                            {t("downloadToView") || "Please download the file to view its contents."}
                        </p>
                        <Button
                            label={t("download") || "Download"}
                            onClick={() => {
                                const link = document.createElement("a");
                                link.href = objectUrl || signedUrl;
                                link.download = fileName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                            variant="primary"
                            className={styles.downloadButton}
                        />
                    </div>
                );
        }
    };

    return ReactDOM.createPortal(
        <div className={styles.overlay}>
            <div className={styles.modal} ref={modalContentRef}>
                <div className={styles.header}>
                    <h2>{t("filePreview") || "File Preview"}</h2>
                </div>

                <div className={`${styles.body} ${styles.previewBody}`}>
                    <div className={styles.filenameHeader}>{fileName}</div>
                    {renderPreview()}
                </div>

                <div className={styles.footer}>
                    <Button label={t("close") || "Close"} onClick={onClose} variant="outline" shape="pill" />
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default FilePreviewModal;
